import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Avatar,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@material-ui/core';
import { uniq } from 'lodash';
import { HelpCircle } from 'lucide-react';
import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { CopilotButton } from '../components/DadoCopilot/CopilotButton';
import { SupportForm } from '../components/SupportForm';
import { SwitchOrganizationDialog } from '../components/SwitchOrganizationDialog';
import { useDialogState } from '../hooks/useDialogState';
import { signOut } from '../services/auth';
import { useCurrentUser } from '../services/currentUser';
import { useNewUx } from '../services/newUx';
import { toFullName } from '../services/user';
import { useCopilotMaybe } from '../components/DadoCopilot/CopilotContext';
import { useCopilotAccess } from '../components/DadoCopilot/useCopilotAccess';

type Props = {};

const Container = styled('div')`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${(p) => p.theme.spacing(2)}px;
`;

const StyledA = styled('a')`
  color: ${(p) => p.theme.palette.primary.main};
  text-decoration: none;
  border-bottom: 2px solid ${(p) => p.theme.palette.primary.main};
`;

const FeedbackFormDialog = ({
  email,
  open,
  onClose
}: {
  email: string;
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <SupportForm
          email={email}
          title="Send us a message"
          disclaimer={false}
          description={
            <p>
              We're happy to receive your feedback, ideas, or questions! You're
              also welcome to check out the{' '}
              <StyledA href="" target="_blank" rel="noreferrer noopener">
                Help Center
              </StyledA>{' '}
              for FAQs and tutorials.
            </p>
          }
          onDone={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};

export const TopNavRight: React.FC<Props> = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const currentUser = useCurrentUser();
  const { enabled: newUxEnabled, acknowledged: newUxAcknowledged, locked: newUxLocked, setEnabled: setNewUxEnabled } = useNewUx();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const onClickMenu = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const onCloseMenu = () => setAnchorEl(null);
  const menuOpen = anchorEl !== null;

  const {
    dialogOpen: orgDialogOpen,
    openDialog: openOrgDialog,
    closeDialog: closeOrgDialog
  } = useDialogState(false);
  const {
    dialogOpen: fbDialogOpen,
    // openDialog: openFbDialog,
    closeDialog: closeFbDialog
  } = useDialogState(false);

  const copilot = useCopilotMaybe();
  const { troubleshooterMode } = useCopilotAccess();

  return (
    <Container>
      <CopilotButton />
      <Button
        startIcon={<HelpCircle size={16} />}
        variant="contained"
        color="primary"
        size="small"
        onClick={() => (window as any).Beacon('open')}
      >
        {t('Help Center')}
      </Button>
      <Popover
        open={menuOpen}
        anchorEl={anchorEl}
        elevation={8}
        onClose={onCloseMenu}
        classes={{
          paper: css({
            width: '220px',
            position: 'relative',
            right: '-120px'
          })
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <List>
          <ListItem>
            <ListItemText secondary={currentUser.org.name} />
          </ListItem>

          {uniq(currentUser.orgs).length > 1 && (
            <ListItem divider button onClick={openOrgDialog}>
              <ListItemText primary={`${t('userMenu.switchOrg')}...`} />
            </ListItem>
          )}
          {troubleshooterMode && (
            <ListItem
              divider
              button
              onClick={() => {
                copilot?.open();
                onCloseMenu();
              }}
            >
              <ListItemText primary={t('userMenu.troubleshooter', 'Troubleshooter')} />
            </ListItem>
          )}
          {newUxAcknowledged && !newUxLocked && (
            <ListItem
              divider
              button
              onClick={() => {
                setNewUxEnabled(!newUxEnabled);
                onCloseMenu();
              }}
            >
              <ListItemText
                primary={
                  newUxEnabled
                    ? t('userMenu.switchToClassic', 'Switch to classic view')
                    : t('userMenu.switchToNewUx', 'Switch to new experience')
                }
              />
            </ListItem>
          )}
          <ListItem button onClick={() => signOut()}>
            <ListItemText primary={t('userMenu.signOut')} />
          </ListItem>
        </List>
      </Popover>
      <ButtonBase color="inherit" onClick={onClickMenu}>
        {!isMobile && (
          <Typography variant="body1" color="inherit">
            {toFullName(currentUser)}
          </Typography>
        )}
        <div style={{ marginLeft: theme.spacing(1) }}>
          <Avatar
            src={currentUser.photoURL || ''}
            style={{ height: 28, width: 28 }}
          />
        </div>
      </ButtonBase>

      <SwitchOrganizationDialog open={orgDialogOpen} onClose={closeOrgDialog} />
      <FeedbackFormDialog
        open={fbDialogOpen}
        onClose={closeFbDialog}
        email={currentUser.email}
      />
    </Container>
  );
};
